import { faDigits, formatMoney } from "@kitchen/ui";
import type { SavedFoodReport } from "@kitchen/schemas";

interface SavedFoodReportPrintProps {
  report: SavedFoodReport;
  onClose: () => void;
}

export function SavedFoodReportPrint({ report, onClose }: SavedFoodReportPrintProps) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.6)",
        zIndex: 200,
        overflowY: "auto",
        padding: "24px 16px",
      }}
    >
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .a4-sheet, .a4-sheet * {
            visibility: visible;
          }
          .a4-sheet {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            margin: 0 !important;
            padding: 15mm !important;
            box-shadow: none !important;
            border: none !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* Action Bar */}
      <div
        className="no-print"
        style={{
          maxWidth: "210mm",
          margin: "0 auto 16px auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#fff",
          padding: "12px 20px",
          borderRadius: 12,
        }}
      >
        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              window.print();
            }}
          >
            چاپ فرم A4
          </button>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            بستن
          </button>
        </div>
        <span style={{ fontSize: 13, color: "#666" }}>پیش‌نمایش چاپ رسمی A4</span>
      </div>

      {/* A4 Sheet */}
      <div
        className="a4-sheet"
        dir="rtl"
        style={{
          width: "210mm",
          minHeight: "297mm",
          margin: "0 auto",
          backgroundColor: "#fff",
          padding: "20mm 15mm",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          boxSizing: "border-box",
          fontFamily: "inherit",
          color: "#111",
        }}
      >
        {/* Header */}
        <div
          style={{
            borderBottom: "2px solid #0f172a",
            paddingBottom: 16,
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, margin: "0 0 4px 0" }}>آشپزخانه صنعتی مرکزی</h1>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: "#475569", margin: 0 }}>
              گزارش رسمی غذای نجات‌یافته (فودپارتی)
            </h2>
          </div>
          <div style={{ textAlign: "left", fontSize: 12, color: "#475569", lineHeight: 1.6 }}>
            <div>
              بازه گزارش: {faDigits(report.periodStart)} تا {faDigits(report.periodEnd)}
            </div>
            <div>تاریخ صدور: {faDigits(new Date().toISOString().slice(0, 10))}</div>
          </div>
        </div>

        {/* Summary KPIs */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
            marginBottom: 24,
          }}
        >
          <div style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>کل پرس‌های نجات‌یافته</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f766e" }}>
              {faDigits(report.totalPortionsSaved)} پرس
            </div>
          </div>
          <div style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>درآمد حاصل از فروش مازاد</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1e40af" }}>
              {formatMoney(report.totalSavedRevenueRial, "toman")}
            </div>
          </div>
          <div style={{ border: "1px solid #cbd5e1", borderRadius: 8, padding: 12, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>ارزش اولیه غذاها</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#475569" }}>
              {formatMoney(report.totalOriginalValueRial, "toman")}
            </div>
          </div>
        </div>

        {/* Detailed Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 32 }}>
          <thead>
            <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #94a3b8" }}>
              <th style={{ padding: "8px 6px", textAlign: "right", border: "1px solid #cbd5e1" }}>ردیف</th>
              <th style={{ padding: "8px 6px", textAlign: "right", border: "1px solid #cbd5e1" }}>تاریخ</th>
              <th style={{ padding: "8px 6px", textAlign: "right", border: "1px solid #cbd5e1" }}>نام غذا</th>
              <th style={{ padding: "8px 6px", textAlign: "center", border: "1px solid #cbd5e1" }}>
                پرس نجات‌یافته
              </th>
              <th style={{ padding: "8px 6px", textAlign: "left", border: "1px solid #cbd5e1" }}>
                درآمد حاصله
              </th>
              <th style={{ padding: "8px 6px", textAlign: "left", border: "1px solid #cbd5e1" }}>
                ارزش اولیه
              </th>
              <th style={{ padding: "8px 6px", textAlign: "left", border: "1px solid #cbd5e1" }}>
                تخفیف پیشگیری از اسراف
              </th>
            </tr>
          </thead>
          <tbody>
            {report.rows.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
                  هیچ رکوردی در این بازه ثبت نشده است.
                </td>
              </tr>
            ) : (
              report.rows.map((row, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "8px 6px", border: "1px solid #cbd5e1", textAlign: "center" }}>
                    {faDigits(idx + 1)}
                  </td>
                  <td style={{ padding: "8px 6px", border: "1px solid #cbd5e1" }}>{faDigits(row.date)}</td>
                  <td style={{ padding: "8px 6px", border: "1px solid #cbd5e1", fontWeight: 600 }}>
                    {row.foodName}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      border: "1px solid #cbd5e1",
                      textAlign: "center",
                      fontWeight: 700,
                    }}
                  >
                    {faDigits(row.portionsSaved)}
                  </td>
                  <td style={{ padding: "8px 6px", border: "1px solid #cbd5e1", textAlign: "left" }}>
                    {formatMoney(row.savedRevenueRial, "toman")}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      border: "1px solid #cbd5e1",
                      textAlign: "left",
                      color: "#64748b",
                    }}
                  >
                    {formatMoney(row.originalValueRial, "toman")}
                  </td>
                  <td
                    style={{
                      padding: "8px 6px",
                      border: "1px solid #cbd5e1",
                      textAlign: "left",
                      color: "#b45309",
                    }}
                  >
                    {formatMoney(row.discountGivenRial, "toman")}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: "#f1f5f9", fontWeight: 700 }}>
              <td
                colSpan={3}
                style={{ padding: "10px 6px", border: "1px solid #cbd5e1", textAlign: "right" }}
              >
                جمع کل:
              </td>
              <td style={{ padding: "10px 6px", border: "1px solid #cbd5e1", textAlign: "center" }}>
                {faDigits(report.totalPortionsSaved)}
              </td>
              <td style={{ padding: "10px 6px", border: "1px solid #cbd5e1", textAlign: "left" }}>
                {formatMoney(report.totalSavedRevenueRial, "toman")}
              </td>
              <td style={{ padding: "10px 6px", border: "1px solid #cbd5e1", textAlign: "left" }}>
                {formatMoney(report.totalOriginalValueRial, "toman")}
              </td>
              <td style={{ padding: "10px 6px", border: "1px solid #cbd5e1", textAlign: "left" }}>
                {formatMoney(report.totalDiscountGivenRial, "toman")}
              </td>
            </tr>
          </tfoot>
        </table>

        {/* Signatures */}
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 48, paddingTop: 16 }}>
          <div style={{ textAlign: "center", width: 160 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 40 }}>مسئول فودپارتی و انبار</div>
            <div style={{ borderTop: "1px dotted #94a3b8", fontSize: 11, color: "#64748b", paddingTop: 4 }}>
              امضاء و تایید
            </div>
          </div>
          <div style={{ textAlign: "center", width: 160 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 40 }}>مدیر مالی و اداری</div>
            <div style={{ borderTop: "1px dotted #94a3b8", fontSize: 11, color: "#64748b", paddingTop: 4 }}>
              امضاء و تایید
            </div>
          </div>
          <div style={{ textAlign: "center", width: 160 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 40 }}>مدیریت ارشد مجموعه</div>
            <div style={{ borderTop: "1px dotted #94a3b8", fontSize: 11, color: "#64748b", paddingTop: 4 }}>
              امضاء و تایید نهایی
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
